import React from 'react';

import { faCheck, faChevronDown, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import { Button } from 'src/registry/ui/Button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'src/registry/ui/Command';
import { FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Popover, PopoverContent, PopoverTrigger } from 'src/registry/ui/Popover';
import { generalMessages } from 'src/registry/utils/messages';
import { cn } from 'src/utils/shadcn';

import type { Control, FieldValues, Path } from 'react-hook-form';
import * as _ from 'lodash';

type SearchSelectFormFieldProps<TItem, TFormValues extends FieldValues> = {
    name: Path<TFormValues>;
    control: Control<TFormValues>;
    label: React.ReactNode;
    items: TItem[];
    getItemValue: (item: TItem) => string | number;
    getItemLabel: (item: TItem) => string;
    getItemKeywords?: (item: TItem) => (string | undefined)[];
    placeholder: string;
    searchPlaceholder: string;
    emptyTitle: React.ReactNode;
    emptyDescription?: React.ReactNode;
    required?: boolean;
    clearable?: boolean;
    disabled?: boolean;
    loadOptions?: (search: string) => void;
    isMultiSelect?: boolean;
};

function SearchSelectFormField<TItem, TFormValues extends FieldValues>({
    name,
    control,
    label,
    items,
    getItemValue,
    getItemLabel,
    getItemKeywords,
    placeholder,
    searchPlaceholder,
    emptyTitle,
    emptyDescription,
    required = true,
    clearable,
    disabled,
    loadOptions,
    isMultiSelect = false,
}: SearchSelectFormFieldProps<TItem, TFormValues>) {
    const { formatMessage } = useIntl();

    const [open, setOpen] = React.useState<boolean>(false);
    const [search, setSearch] = React.useState<string>('');

    const throttleHandler = React.useMemo(() => {
        if (!loadOptions) return null;

        return _.throttle((value: string) => {
            loadOptions(value);
        }, 300);
    }, [loadOptions]);

    React.useEffect(() => {
        if (!open || !throttleHandler) return;

        throttleHandler(search);
        return () => {
            throttleHandler.cancel();
        };
    }, [search, open, throttleHandler]);

    return (
        <FormField
            control={control}
            name={name}
            render={({ field }) => {
                const value = field.value;

                const selectedItemsArray = isMultiSelect
                    ? items.filter((item) => (field.value as string[])?.includes(String(getItemValue(item))))
                    : [];

                const selectedItem = !isMultiSelect
                    ? items.find((item) => getItemValue(item) === field.value)
                    : undefined;

                const handleSelect = (selectedValue: string) => {
                    if (isMultiSelect) {
                        const current = (value as string[]) || [];

                        const exists = current.includes(selectedValue);

                        const newValue = exists
                            ? current.filter((v) => v !== selectedValue)
                            : [...current, selectedValue];
                        field.onChange(newValue);
                        setSearch('');
                    } else {
                        field.onChange(selectedValue);
                        setOpen(false);
                        setSearch('');
                    }
                };

                return (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required={required}>
                            {label}
                        </FormLabel>

                        <FormControlStack>
                            <FormControl>
                                <Popover
                                    open={open}
                                    onOpenChange={(isOpen) => {
                                        setOpen(isOpen);
                                        if (!isOpen) {
                                            setTimeout(() => setSearch(''), 200);
                                        }
                                    }}
                                >
                                    <PopoverTrigger asChild disabled={disabled}>
                                        <Button
                                            variant="dropdown"
                                            role="combobox"
                                            aria-expanded={open}
                                            onBlur={field.onBlur}
                                        >
                                            <FontAwesomeIcon
                                                icon={faChevronDown}
                                                className="text-primary-700 mr-1 h-3 w-4 shrink-0"
                                            />
                                            {isMultiSelect ? (
                                                selectedItemsArray?.length ? (
                                                    <div className="truncate">
                                                        {selectedItemsArray
                                                            .map((item) => getItemLabel(item))
                                                            .join(', ')}
                                                    </div>
                                                ) : (
                                                    <p className="text-gray-400">{placeholder}</p>
                                                )
                                            ) : selectedItem ? (
                                                getItemLabel(selectedItem)
                                            ) : (
                                                <p className="text-gray-400">{placeholder}</p>
                                            )}
                                        </Button>
                                    </PopoverTrigger>

                                    <PopoverContent
                                        style={{ width: 'var(--radix-popover-trigger-width)' }}
                                        align="start"
                                        className="p-0 pb-12"
                                        onOpenAutoFocus={(e) => e.preventDefault()}
                                        onInteractOutside={(e) => {
                                            const target = e.target as HTMLElement;
                                            if (target.closest('[cmdk-root]')) {
                                                e.preventDefault();
                                            }
                                        }}
                                    >
                                        <Command shouldFilter={false}>
                                            <CommandInput
                                                value={search}
                                                onValueChange={setSearch}
                                                placeholder={searchPlaceholder}
                                                maxLength={255}
                                                autoFocus
                                            />

                                            <CommandList>
                                                <CommandEmpty className="py-6 text-center">
                                                    <p className="text-sm font-semibold">{emptyTitle}</p>
                                                    {emptyDescription && (
                                                        <p className="text-sm text-gray-500">{emptyDescription}</p>
                                                    )}
                                                </CommandEmpty>

                                                <CommandGroup
                                                    className={cn(
                                                        'max-h-40 overflow-y-auto',
                                                        !items.length && 'hidden',
                                                    )}
                                                >
                                                    {items.map((item) => {
                                                        const optionValue = String(getItemValue(item));

                                                        const isSelected = isMultiSelect
                                                            ? (value as string[])?.includes(optionValue)
                                                            : value === optionValue;
                                                        return (
                                                            <CommandItem
                                                                key={optionValue}
                                                                value={optionValue}
                                                                keywords={
                                                                    getItemKeywords?.(item)?.filter(Boolean) as string[]
                                                                }
                                                                onSelect={() => {
                                                                    handleSelect(optionValue);
                                                                    if (!isMultiSelect) {
                                                                        setOpen(false);
                                                                    }
                                                                }}
                                                                className={cn(
                                                                    'flex cursor-default items-center rounded-xs px-2 py-1.5 pl-8 text-sm outline-hidden select-none focus:bg-gray-100 data-[state=open]:bg-gray-100 dark:focus:bg-gray-800 dark:data-[state=open]:bg-gray-800',
                                                                )}
                                                            >
                                                                <span className="absolute left-2 flex size-3.5 items-center justify-center">
                                                                    {isSelected && (
                                                                        <FontAwesomeIcon
                                                                            icon={faCheck}
                                                                            className="text-primary-700"
                                                                        />
                                                                    )}
                                                                </span>
                                                                <div>{getItemLabel(item)}</div>
                                                            </CommandItem>
                                                        );
                                                    })}
                                                </CommandGroup>

                                                {clearable && (
                                                    <CommandGroup
                                                        forceMount
                                                        className="absolute bottom-0 left-0 z-50 w-full rounded-b border bg-white"
                                                    >
                                                        <CommandItem
                                                            onSelect={() => {
                                                                field.onChange(isMultiSelect ? [] : '');
                                                                setSearch('');
                                                                setOpen(false);
                                                            }}
                                                            className="cursor-pointer"
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faTrashAlt}
                                                                className="mr-1 size-3 w-4 shrink-0 text-red-500"
                                                            />
                                                            {formatMessage(generalMessages.clearSelection)}
                                                        </CommandItem>
                                                    </CommandGroup>
                                                )}
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                            </FormControl>

                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                );
            }}
        />
    );
}

export default SearchSelectFormField;
